import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { useRef, useState, useCallback, useEffect } from 'react';
import { isSameMonth, isWithinInterval, startOfMonth } from 'date-fns';
import Icon from '../Icon/Icon.js';
import MonthBlock from './MonthBlock.js';
import { getFirstSelectedMonthIndex, useNrOfMonthsDisplayedDesktop, isSmallScreen, isMonthSelected } from './utils.js';
import { DatePickerWrapper, DatePickerInnerWrapper, DatePickerHeader, DatePickerNav, DatePickerBody, DatePickerMonths, MonthHeaderMobileSticky } from './style.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import useEffectOnce from '../../hooks/useEffectOnce.js';
import { UI_CHEVRON_LEFT } from '../../icons/UI_CHEVRON_LEFT.js';
import { UI_CHEVRON_RIGHT } from '../../icons/UI_CHEVRON_RIGHT.js';

var _excluded = ["disableStickyMonthHeader", "monthFooter", "nrOfMonths", "offers", "onChange", "onDynamicRangeChange", "recommendedDates", "selectedDate", "selectedDateInterval", "selectedMonths", "startDate", "useDynamicRangeSelect", "validDates"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var DatePicker = function DatePicker(_ref) {
  var disableStickyMonthHeader = _ref.disableStickyMonthHeader,
    monthFooter = _ref.monthFooter,
    nrOfMonths = _ref.nrOfMonths,
    offers = _ref.offers,
    onChange = _ref.onChange,
    onDynamicRangeChange = _ref.onDynamicRangeChange,
    recommendedDates = _ref.recommendedDates,
    selectedDate = _ref.selectedDate,
    selectedDateInterval = _ref.selectedDateInterval,
    selectedMonths = _ref.selectedMonths,
    startDate = _ref.startDate,
    useDynamicRangeSelect = _ref.useDynamicRangeSelect,
    validDates = _ref.validDates,
    rest = _objectWithoutProperties(_ref, _excluded);
  var listRef = useRef(null);
  var datePickerWrapperRef = useRef(null);
  var datePickerBodyRef = useRef(null);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    datePickerBodyElement = _useState2[0],
    setDatePickerBody = _useState2[1];
  var _useState3 = useState(selectedDateInterval),
    _useState4 = _slicedToArray(_useState3, 2),
    internalDateInterval = _useState4[0],
    setInternalDateInterval = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    dateHovered = _useState6[0],
    setDateHovered = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    dynamicRangeInProgress = _useState8[0],
    setDynamicRangeInProgress = _useState8[1];
  var _useState9 = useState(getFirstSelectedMonthIndex(selectedMonths, selectedDate, selectedDateInterval, startDate)),
    _useState10 = _slicedToArray(_useState9, 2),
    monthInLeftPos = _useState10[0],
    setMonthInLeftPos = _useState10[1];
  var nrOfMonthsDisplayedDesktop = useNrOfMonthsDisplayedDesktop(datePickerWrapperRef);
  var navigatePrev = useCallback(function () {
    setMonthInLeftPos(monthInLeftPos - 1);
  }, [setMonthInLeftPos, monthInLeftPos]);
  var navigateNext = useCallback(function () {
    setMonthInLeftPos(monthInLeftPos + 1);
  }, [setMonthInLeftPos, monthInLeftPos]);
  useEffectOnce(function () {
    if (!datePickerBodyElement) setDatePickerBody(datePickerBodyRef.current);
  });
  useEffect(function () {
    setInternalDateInterval(selectedDateInterval);
  }, [selectedDateInterval]);
  useEffect(function () {
    if (isSmallScreen()) {
      setTimeout(function () {
        return listRef.current.children[monthInLeftPos].scrollIntoView();
      }, 100);
    }
  }, [monthInLeftPos, listRef]);
  var handleOnChange = useCallback(function (e, dateStr) {
    if (!useDynamicRangeSelect) {
      onChange(e, dateStr);
    } else {
      var from = internalDateInterval.from,
        to = internalDateInterval.to;
      if (!!from && !!to) {
        setInternalDateInterval({
          from: dateStr,
          to: null
        });
        setDynamicRangeInProgress(true);
        setDateHovered(dateStr);
        onDynamicRangeChange(e, {
          from: dateStr,
          to: null,
          inProgress: true
        });
      } else {
        setDynamicRangeInProgress(false);
        setInternalDateInterval({
          from: from,
          to: dateStr
        });
        setDateHovered(null);
        onDynamicRangeChange(e, {
          from: from,
          to: dateStr,
          inProgress: false
        });
      }
    }
  }, [internalDateInterval, onChange, onDynamicRangeChange, useDynamicRangeSelect]);
  var renderMonths = function renderMonths() {
    var months = [];
    var selectedDateObj = new Date(selectedDate);
    var startDateObj = new Date(startDate);
    var startMonth = startDateObj.getMonth();
    var startYear = startDateObj.getFullYear();
    var _ref2 = (datePickerBodyElement === null || datePickerBodyElement === void 0 ? void 0 : datePickerBodyElement.getBoundingClientRect()) || {},
      left = _ref2.left,
      right = _ref2.right;
    var _loop = function _loop() {
      var _offers$find;
      var monthIndex = startMonth + index;
      var currentMonth = monthIndex % 12;
      var addYear = Math.floor(monthIndex / 12);
      var firstOfMonth = new Date(startYear + addYear, currentMonth, 1);
      var year = firstOfMonth.getFullYear();
      var offersInMonth = ((_offers$find = offers.find(function (monthOffer) {
        return isSameMonth(firstOfMonth, new Date(monthOffer.month));
      })) === null || _offers$find === void 0 ? void 0 : _offers$find.offers) || [];
      var monthIsSelected = (selectedMonths === null || selectedMonths === void 0 ? void 0 : selectedMonths.length) > 0 && isMonthSelected(firstOfMonth, selectedMonths);

      /*
        isWithinSelected and isInHoveredRange is used
        to prevent re-renders for months not affected
      */
      var isWithinSelected = selectedDateObj.getFullYear() === year && selectedDateObj.getMonth() === currentMonth;
      var isInHoveredRange = dynamicRangeInProgress && dateHovered ? isWithinInterval(firstOfMonth, {
        start: startOfMonth(new Date(internalDateInterval.from)),
        end: new Date(dateHovered)
      }) : false;
      var datePickerHasOffers = (offers === null || offers === void 0 ? void 0 : offers.length) > 0;
      var showRecommendedDates = !datePickerHasOffers && !useDynamicRangeSelect;
      var isVisible = index >= monthInLeftPos && index < monthInLeftPos + nrOfMonthsDisplayedDesktop;
      months.push(/*#__PURE__*/jsx(MonthBlock, {
        selectedDate: isWithinSelected && !useDynamicRangeSelect ? selectedDate : null,
        selectedDateInterval: internalDateInterval,
        onChange: handleOnChange,
        monthFooter: monthFooter,
        month: currentMonth,
        year: year,
        validDates: validDates,
        recommendedDates: showRecommendedDates ? recommendedDates : [],
        useDynamicRangeSelect: useDynamicRangeSelect,
        dateHovered: isInHoveredRange ? dateHovered : null,
        setDateHovered: setDateHovered,
        dynamicRangeInProgress: dynamicRangeInProgress,
        offers: (offersInMonth === null || offersInMonth === void 0 ? void 0 : offersInMonth.length) > 0 ? offersInMonth : null,
        datePickerHasOffers: datePickerHasOffers,
        datePickerHasSelectedMonths: (selectedMonths === null || selectedMonths === void 0 ? void 0 : selectedMonths.length) > 0,
        monthIsSelected: monthIsSelected,
        nrOfMonthsDisplayedDesktop: nrOfMonthsDisplayedDesktop,
        navigateToNextMonth: navigateNext,
        navigateToPrevMonth: navigatePrev,
        isMonthBlockVisible: isVisible,
        datePickerBodyPosition: {
          left: left,
          right: right
        }
      }, "".concat(year, "-").concat(currentMonth)));
    };
    for (var index = 0; index < nrOfMonths; index += 1) {
      _loop();
    }
    return months;
  };
  var leftScroll = nrOfMonthsDisplayedDesktop === 3 ? monthInLeftPos * 33.33 : monthInLeftPos * 50;
  return /*#__PURE__*/jsx(DatePickerWrapper, _objectSpread(_objectSpread({
    ref: datePickerWrapperRef
  }, rest), {}, {
    children: /*#__PURE__*/jsxs(DatePickerInnerWrapper, {
      children: [/*#__PURE__*/jsxs(DatePickerHeader, {
        children: [/*#__PURE__*/jsx(DatePickerNav, {
          hidden: monthInLeftPos <= 0,
          onClick: navigatePrev,
          className: "sg-datepicker__nav-prev",
          type: "button",
          children: /*#__PURE__*/jsx(Icon, {
            path: UI_CHEVRON_LEFT,
            size: "xs"
          })
        }), /*#__PURE__*/jsx(DatePickerNav, {
          hidden: monthInLeftPos >= nrOfMonths - 2,
          onClick: navigateNext,
          className: "sg-datepicker__nav-next",
          type: "button",
          children: /*#__PURE__*/jsx(Icon, {
            path: UI_CHEVRON_RIGHT,
            size: "xs"
          })
        })]
      }), /*#__PURE__*/jsx(DatePickerBody, {
        ref: datePickerBodyRef,
        children: /*#__PURE__*/jsxs(DatePickerMonths, {
          ref: listRef,
          style: {
            transform: "translateX(-".concat(isSmallScreen() ? 0 : leftScroll, "%)")
          },
          children: [!disableStickyMonthHeader && /*#__PURE__*/jsx(MonthHeaderMobileSticky, {}), renderMonths()]
        })
      })]
    })
  }));
};
DatePicker.defaultProps = {
  nrOfMonths: 3,
  validDates: [],
  recommendedDates: [],
  selectedDate: '',
  selectedDateInterval: {},
  selectedMonths: [],
  onChange: function onChange() {},
  onDynamicRangeChange: function onDynamicRangeChange() {},
  monthFooter: function monthFooter() {},
  useDynamicRangeSelect: false,
  offers: [],
  disableStickyMonthHeader: false
};
var DatePicker$1 = DatePicker;

export { DatePicker$1 as default };
//# sourceMappingURL=DatePicker.js.map
